/** Module 7 (`EPN.PayrollReview.Api`) payroll-run status. The API serializes it as a string
 * (`Status.ToString()` — see `PayrollRunService.ToResponse`), unlike the numeric enums used by some other
 * EPN modules. Typed as `string` on the response so an unexpected future status is displayed, never crashes. */
export const PayrollRunStatus = {
  Draft: 'Draft',
  PendingApproval: 'PendingApproval',
  Approved: 'Approved',
  Rejected: 'Rejected',
  /** EPN-241: the run's own preparer took it back while it was pending. Who, when and why use the `rejected*` fields. */
  Withdrawn: 'Withdrawn',
} as const;

/** Mirrors `PayrollRunResponse` (`EPN.PayrollReview.Api/Features/PayrollRuns/PayrollRunContracts.cs`)
 * field-for-field. Dates are `yyyy-MM-dd` (DateOnly); `*AtUtc` values are ISO-8601 instants. */
export interface PayrollReviewRun {
  id: string;
  companyId: string;
  payScheduleId: string | null;
  periodStartDate: string;
  periodEndDate: string;
  checkDate: string;
  status: string;
  createdByUserId: string;
  createdByEmailSnapshot: string;
  createdAtUtc: string;
  submittedByUserId: string | null;
  submittedByEmailSnapshot: string | null;
  submittedForApprovalAtUtc: string | null;
  approvedByUserId: string | null;
  approvedByEmailSnapshot: string | null;
  approvedAtUtc: string | null;
  rejectedByUserId: string | null;
  rejectedByEmailSnapshot: string | null;
  rejectedAtUtc: string | null;
  rejectionReason: string | null;
  /** EPN-22 prompt 3: the run's tax readiness for approval (present on the run read, submit and approve; absent on lists). */
  taxReadiness?: PayrollRunTaxReadiness | null;
  /** The outcome the approval recorded: `Ready`, `NotReported`, or null for an approval from before the check. */
  approvedTaxReadinessOutcome?: string | null;
}

/** Mirrors `TaxReadinessResponse`. `NotReady` means the server refuses approval (409 `TAX_NOT_PRODUCTION_READY`); `NotReported`
 * means a provider reports no readiness (the Mock, Symmetry), so the check does not apply. Employee ids and EPN codes only. */
export interface PayrollRunTaxReadiness {
  status: 'Ready' | 'NotReady' | 'NotReported' | string;
  calculationCount: number;
  notReadyCount: number;
  notReportedCount: number;
  blocked: { employeeId: string; codes: string[] }[];
}

/** EPN-22: one tax-engine warning on an attached calculation. EPN's code and EPN's own wording (never vendor text). Informational only:
 * a warning never blocks approval. */
export interface PayrollRunTaxWarning {
  code: string;
  message: string;
}

/** Mirrors `PayrollRunCalculationResponse` (`GET /api/payroll-runs/{id}/calculations`). `taxWarnings` is omitted when there are none. */
export interface PayrollRunCalculation {
  id: string;
  payrollRunId: string;
  payrollCalculationId: string;
  employeeId: string;
  netPayAmount: number;
  createdAtUtc: string;
  taxReadiness?: string | null;
  taxBlockerCodes?: string[] | null;
  taxWarnings?: PayrollRunTaxWarning[];
}
